import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { meApi, type WorkspaceKey } from '../../api';
import { t } from '../../i18n';
import { errorText } from '../../lib/errors';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toast';
import { useWorkspace } from '../../stores/workspace';
import { Segmented } from '../ui';
import { Dropdown } from '../ui/Dropdown';
import { IconSearch } from '../ui/Icon';
import { NotificationBell } from '../NotificationBell';
import { helpFor, openHelp } from '../HelpCards';
import { crumbs } from './nav';
import { partnerCompanyName } from './tenant';
import { myWorkspaceKeys, WORKSPACE_BY_KEY } from './workspaces';

const ALL = 'ALL';

/** Перемикач країни: фільтрує всю CRM на сервері, запамʼятовується на людину. */
export function CountrySwitcher() {
  const me = useAuth((s) => s.me);
  const setScope = useAuth((s) => s.setScope);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const scope = me?.scope;
  if (!scope || scope.countries.length === 0) return null;
  if (scope.countries.length === 1) {
    return <span className="chip !h-8 !px-3 font-semibold text-fg shrink-0" title={t('scope.onlyCountry')}>{scope.countries[0]}</span>;
  }
  const value = scope.country ?? ALL;
  const change = async (v: string) => {
    if (v === value) return;
    setBusy(true);
    try {
      const next = await meApi.putPrefs({ country: v === ALL ? null : v });
      setScope(next);
      // усі списки залежать від області — перечитуємо все, крім довідників
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'meta' });
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Segmented
      label={t('scope.label')}
      // у верхній панелі не стискається і не переноситься: інакше на вузькому екрані стає стовпцем і розтягує панель
      className="shrink-0 flex-nowrap"
      value={value}
      onChange={(v) => void change(v)}
      disabled={busy}
      items={[{ value: ALL, label: t('common.all') }, ...scope.countries.map((c) => ({ value: c, label: c }))]}
    />
  );
}

/** Перемикач робочого простору: показуємо, коли їх у людини кілька; один — просто назва. */
export function WorkspaceSwitcher() {
  const me = useAuth((s) => s.me);
  const current = useWorkspace((s) => s.current);
  const setCurrent = useWorkspace((s) => s.setCurrent);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const keys = myWorkspaceKeys(me);
  if (keys.length === 0) return null;
  const value = current && keys.includes(current) ? current : keys[0]!;
  if (keys.length === 1) {
    return <span className="chip !h-8 !px-3 font-semibold text-fg shrink-0 hidden sm:inline-flex items-center">{t(WORKSPACE_BY_KEY[value].titleKey)}</span>;
  }
  const change = async (v: string) => {
    if (v === current) return;
    setBusy(true);
    try {
      await setCurrent(v as WorkspaceKey, { navigate });
    } catch {
      toast.error(t('ws.switchFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dropdown
      label={t('ws.switch')}
      value={value}
      disabled={busy}
      onChange={(v) => void change(v)}
      options={keys.map((k) => ({ value: k, label: t(WORKSPACE_BY_KEY[k].titleKey) }))}
    />
  );
}

export function TopBar({ onSearch }: { onSearch: () => void }) {
  const { pathname, search } = useLocation();
  const current = useWorkspace((s) => s.current);
  const path = crumbs(pathname, current, search);
  const partner = partnerCompanyName(useAuth((s) => s.me));
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <div className="flex gap-3 items-center justify-between mb-4 sm:mb-6">
      <div className="hidden sm:block text-sm text-lab min-w-0 truncate">
        {path.map((k, i) => (
          <span key={k}>
            {i > 0 && ' / '}
            {i === path.length - 1 ? <b className="text-fg font-semibold">{t(k)}</b> : t(k)}
          </span>
        ))}
      </div>
      {/* на телефоні перемикачі простору й країни разом ширші за екран — переносимо рядок, а не розтягуємо сторінку вбік */}
      <div className="flex flex-wrap sm:flex-nowrap justify-end gap-2.5 items-center ml-auto sm:ml-0 min-w-0 w-full sm:w-auto sm:shrink">
        {/* партнерська компанія: назва тенанта поруч із перемикачами, щоб було видно, в якій компанії працюєте */}
        {partner && (
          <span className="chip !h-8 !px-3 font-semibold text-fg max-w-[220px] truncate hidden sm:inline-flex items-center" title={partner}>
            {partner}
          </span>
        )}
        <WorkspaceSwitcher />
        <CountrySwitcher />
        {helpFor(pathname) && (
          <button
            type="button"
            onClick={openHelp}
            className="flex items-center gap-2 h-10 px-3 rounded-full border border-line bg-bg text-sm font-semibold hover:border-line-strong shrink-0"
            title="Як користуватись цим екраном"
            aria-label="Як користуватись цим екраном"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01" />
            </svg>
            <span className="hidden xl:inline">Як користуватись</span>
          </button>
        )}
        <NotificationBell />
        <button
          type="button"
          onClick={onSearch}
          className="flex items-center gap-2 h-10 px-3 rounded border border-line bg-bg text-sm text-lab hover:border-line-strong shrink-0 sm:shrink sm:min-w-10 sm:w-[260px] md:w-10 md:px-0 md:justify-center lg:w-[260px] lg:px-3 lg:justify-start"
          title={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          aria-keyshortcuts={mac ? 'Meta+K' : 'Control+K'}
        >
          <IconSearch size={16} />
          <span className="hidden sm:inline md:hidden lg:inline flex-1 text-left truncate">{t('search.placeholder')}</span>
          <kbd className="hidden sm:inline md:hidden lg:inline font-sans text-xs">{mac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
      </div>
    </div>
  );
}
